import { useEffect, useRef, useState } from "react";
import {
  Reply,
  Copy,
  Smile,
  CornerUpRight,
  Pin,
  PinOff,
  Star,
  StarOff,
  Trash2,
  Plus,
} from "lucide-react";
import type { Message } from "../../lib/api";

interface MessageContextMenuProps {
  message: Message;
  position: { x: number; y: number };
  onClose: () => void;
  onReply: (msg: Message) => void;
  onCopy: (text: string, id: string) => void;
  onReact: (msg: Message, emoji: string) => void;
  onForward: (msg: Message) => void;
  onTogglePin: (msg: Message) => void;
  onToggleStar: (msg: Message) => void;
  onDelete: (msg: Message) => void;
}

const DEFAULT_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];
const EXTENDED_REACTIONS = ["🔥", "🎉", "👏", "💯", "🤝", "😍", "🥳", "🤔", "👀", "🚀", "💔", "✨"];

export function MessageContextMenu({
  message,
  position,
  onClose,
  onReply,
  onCopy,
  onReact,
  onForward,
  onTogglePin,
  onToggleStar,
  onDelete,
}: MessageContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [showExtendedReactions, setShowExtendedReactions] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: position.y, left: position.x });

  const isStarred = Boolean(message.content?.is_starred);
  const isPinned = Boolean(message.content?.is_pinned);
  const currentReactions: Array<{ emoji: string; from_me?: boolean }> = message.content?.reactions || [];
  const myReaction = currentReactions.find((r) => r.from_me)?.emoji;

  // Calculate clamped position within viewport
  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const padding = 12;

    let top = position.y;
    let left = position.x;

    if (top + rect.height > window.innerHeight - padding) {
      top = Math.max(padding, window.innerHeight - rect.height - padding);
    }
    if (left + rect.width > window.innerWidth - padding) {
      left = Math.max(padding, window.innerWidth - rect.width - padding);
    }

    setCoords({ top, left });
  }, [position, showExtendedReactions]);

  // Click outside and escape key handling
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleEmojiClick = (emoji: string) => {
    onReact(message, emoji);
    onClose();
  };

  return (
    <div
      ref={menuRef}
      style={{ top: `${coords.top}px`, left: `${coords.left}px` }}
      className="fixed z-50 min-w-[210px] bg-[#233138] text-[#d1d7db] rounded-xl shadow-2xl border border-slate-700/60 py-1.5 backdrop-blur-md animate-in fade-in zoom-in-95 duration-100 select-none text-[13px]"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Floating Emoji Reaction Bar */}
      <div className="px-2 pt-1 pb-2 border-b border-slate-700/60">
        <div className="flex items-center justify-between gap-1 bg-[#182229] p-1 rounded-full border border-slate-700/50">
          {DEFAULT_REACTIONS.map((emoji) => {
            const isSelected = myReaction === emoji;
            return (
              <button
                key={emoji}
                type="button"
                onClick={() => handleEmojiClick(emoji)}
                className={`w-7 h-7 flex items-center justify-center rounded-full text-base transition-transform hover:scale-125 active:scale-95 ${
                  isSelected ? "bg-slate-700/80 ring-1 ring-emerald-500 scale-110" : "hover:bg-slate-700/40"
                }`}
                title={`React with ${emoji}`}
              >
                {emoji}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setShowExtendedReactions(!showExtendedReactions)}
            className={`w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-200 transition-colors ${
              showExtendedReactions ? "bg-slate-700 text-slate-200" : "hover:bg-slate-700/40"
            }`}
            title="More reactions"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Extended Reactions Drawer */}
        {showExtendedReactions && (
          <div className="mt-2 p-1.5 bg-[#182229] rounded-xl border border-slate-700/50 grid grid-cols-6 gap-1 animate-in fade-in duration-100">
            {EXTENDED_REACTIONS.map((emoji) => {
              const isSelected = myReaction === emoji;
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => handleEmojiClick(emoji)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg text-base hover:scale-120 transition-transform ${
                    isSelected ? "bg-slate-700 ring-1 ring-emerald-500" : "hover:bg-slate-700/40"
                  }`}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Menu Actions */}
      <div className="py-1">
        {/* Reply */}
        <button
          type="button"
          onClick={() => {
            onReply(message);
            onClose();
          }}
          className="w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-[#182229] hover:text-white transition-colors"
        >
          <Reply className="w-4 h-4 text-slate-400" />
          <span>Reply</span>
        </button>

        {/* Copy */}
        {message.content?.text && (
          <button
            type="button"
            onClick={() => {
              onCopy(message.content.text, message.id);
              onClose();
            }}
            className="w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-[#182229] hover:text-white transition-colors"
          >
            <Copy className="w-4 h-4 text-slate-400" />
            <span>Copy</span>
          </button>
        )}

        {/* React */}
        <button
          type="button"
          onClick={() => setShowExtendedReactions((prev) => !prev)}
          className="w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-[#182229] hover:text-white transition-colors"
        >
          <Smile className="w-4 h-4 text-slate-400" />
          <span>React</span>
        </button>

        {/* Forward */}
        <button
          type="button"
          onClick={() => {
            onForward(message);
            onClose();
          }}
          className="w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-[#182229] hover:text-white transition-colors"
        >
          <CornerUpRight className="w-4 h-4 text-slate-400" />
          <span>Forward</span>
        </button>

        {/* Pin / Unpin */}
        <button
          type="button"
          onClick={() => {
            onTogglePin(message);
            onClose();
          }}
          className="w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-[#182229] hover:text-white transition-colors"
        >
          {isPinned ? (
            <>
              <PinOff className="w-4 h-4 text-amber-400" />
              <span>Unpin</span>
            </>
          ) : (
            <>
              <Pin className="w-4 h-4 text-slate-400" />
              <span>Pin</span>
            </>
          )}
        </button>

        {/* Star / Unstar */}
        <button
          type="button"
          onClick={() => {
            onToggleStar(message);
            onClose();
          }}
          className="w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-[#182229] hover:text-white transition-colors"
        >
          {isStarred ? (
            <>
              <StarOff className="w-4 h-4 text-amber-400" />
              <span>Unstar</span>
            </>
          ) : (
            <>
              <Star className="w-4 h-4 text-slate-400" />
              <span>Star</span>
            </>
          )}
        </button>

        <div className="my-1 border-t border-slate-700/60" />

        {/* Delete */}
        <button
          type="button"
          onClick={() => {
            onDelete(message);
            onClose();
          }}
          className="w-full px-4 py-2 flex items-center gap-3 text-left text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          <span>Delete</span>
        </button>
      </div>
    </div>
  );
}
